import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { useMount } from 'ahooks';
import { Space } from 'antd';
import { useAppDispatch } from 'app/hooks';
import {
  SectionHeading,
  TextHeavy,
  Text,
  UnderlineText,
} from 'components/RevxTypography';
import { PartialSKU, SkuSize, StepActionTypeEnum } from 'domains/skuDomain';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpecifySuppliers } from '../components/SpecifySuppliers';

const SIZES_DEFAULT_VISIBLE_COUNT = 7;

type Props = {
  sku: PartialSKU;
};

export const Step1InitialOverviewSection = ({ sku }: Props) => {
  const { displaySize } = useMeasurementUnit();
  const { t } = useTranslation();
  const curItem = sku;
  const dispatch = useAppDispatch();

  useMount(() => {
    dispatch.sku.registerStepAction({
      [StepActionTypeEnum.STEP1_QUANTITIES]: submit,
    });
  });

  const submit = async (v: any) => {
    try {
      await dispatch.sku.saveSku({
        ...curItem,
        isSpecifySupplier: v.isSpecifySupplier,
        specifySupplierOrgIds: v.specifySupplierOrgIds || [],
      });
      return true;
    } catch (e) {
      return false;
    }
  };

  const [displaySkuSizes, setDisplaySkuSizes] = useState<SkuSize[]>(
    (curItem.skuSizes || []).slice(0, SIZES_DEFAULT_VISIBLE_COUNT)
  );

  return (
    <>
      <div className="multi-sku-config-wizard-step-title">
        <span className="brand-green">{'Summary: Overview'}</span>
      </div>
      <div>
        <SpecifySuppliers
          sku={sku}
          cb={async (v) => {
            await submit(v);
          }}
        />
      </div>
      <div className="multi-sku-config-wizard-step-content">
        <div className="multi-sku-overview">
          <div className="multi-sku-overview-item">
            <div className="multi-sku-overview-item-label">
              {t('platform.sku.form.label.sizes')}
            </div>
            <div className="multi-sku-overview-item-content">
              <ul>
                <li>
                  <strong>
                    {t('platform.sku.publishReview.optionTitle.orientations')}:
                  </strong>
                  [{(curItem.orientation || []).join(', ')}]
                </li>
                {displaySkuSizes.map((sizeItem, index) => {
                  return (
                    <li key={index}>
                      <Space>
                        <span>
                          <TextHeavy>
                            {t(
                              'platform.sku.publishReview.optionTitle.finishedSize'
                            )}
                            :{' '}
                          </TextHeavy>
                          <Text>{displaySize(sizeItem.finishedSize, { metric: sizeItem.finishedSizeMetric })}</Text> {',   '}
                        </span>
                        <span>
                          <TextHeavy>
                            {t(
                              'platform.sku.publishReview.optionTitle.flatSize'
                            )}
                            :{' '}
                          </TextHeavy>
                          <Text>{displaySize(sizeItem.flatSize, { metric: sizeItem.flatSizeMetric })}</Text>
                          {',   '}
                        </span>
                        <span>
                          <TextHeavy>
                            {t(
                              'platform.sku.publishReview.optionTitle.orientation'
                            )}
                            :{' '}
                          </TextHeavy>
                          <Text>{sizeItem.orientation}</Text>
                        </span>
                      </Space>
                    </li>
                  );
                })}
              </ul>
              {(curItem.skuSizes || []).filter((item) =>
                sku.orientation?.includes(item.orientation)
              ).length > SIZES_DEFAULT_VISIBLE_COUNT && (
                <div>
                  <UnderlineText
                    color="blue"
                    onClick={() => {
                      const skuSizes = (curItem.skuSizes || []).filter((item) =>
                        sku.orientation?.includes(item.orientation)
                      );
                      if (displaySkuSizes.length >= skuSizes.length) {
                        setDisplaySkuSizes(
                          skuSizes.slice(0, SIZES_DEFAULT_VISIBLE_COUNT)
                        );
                      } else {
                        setDisplaySkuSizes(skuSizes);
                      }
                    }}
                  >
                    {displaySkuSizes.length >=
                    (curItem.skuSizes || []).filter((item) =>
                      sku.orientation?.includes(item.orientation)
                    ).length
                      ? t('platform.sku.form.btn.viewLessSizes')
                      : t('platform.sku.form.btn.viewAllSizes')}
                  </UnderlineText>
                </div>
              )}

              {curItem.customSizeEnable && (
                <div>
                  <Text>{t('platform.sku.form.tip.customSizesAvailable')}</Text>
                </div>
              )}
            </div>
          </div>
          <div className="multi-sku-overview-item">
            <div className="multi-sku-overview-item-label">
              {t('platform.sku.form.label.requiredServices')}
            </div>
            <div className="multi-sku-overview-item-content">
              {(curItem?.requiredSvcTypes || []).length > 0 && (
                <>
                  <SectionHeading>
                    {t('platform.sku.serviceTypes')}:
                  </SectionHeading>
                  <ul>
                    {(curItem?.requiredSvcTypes || []).map(
                      (s, index: number) => {
                        return (
                          <li key={index}>
                            <TextHeavy>{s.name}</TextHeavy>
                          </li>
                        );
                      }
                    )}
                    {(curItem?.exceptRequiredSvcs || []).length > 0 && (
                      <li>
                        <SectionHeading style={{ marginTop: 10 }}>
                          {t('platform.sku.exceptions')}:
                        </SectionHeading>
                        <ul>
                          {(curItem?.exceptRequiredSvcs || []).map((svc) => {
                            return <li key={svc.id}>{svc.name}</li>;
                          })}
                        </ul>
                      </li>
                    )}
                  </ul>
                </>
              )}

              {(curItem?.requiredSvcs || []).length > 0 && (
                <>
                  <SectionHeading style={{ marginTop: 10 }}>
                    {t('platform.sku.services')}:
                  </SectionHeading>
                  <ul>
                    {(curItem?.requiredSvcs || []).map((s, index: number) => {
                      return (
                        <li key={index}>
                          <TextHeavy>{s.name}</TextHeavy>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          </div>
          <div className="multi-sku-overview-item">
            <div className="multi-sku-overview-item-label">
              {t('platform.sku.form.label.optionalServices')}
            </div>
            <div className="multi-sku-overview-item-content">
              <SectionHeading>{t('platform.sku.serviceTypes')}:</SectionHeading>
              <ul>
                {(curItem?.optionalSvcTypes || []).map((s, index: number) => {
                  return (
                    <li key={index}>
                      <TextHeavy>{s.name}</TextHeavy>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* <div className="multi-sku-overview-item">
            <div className="multi-sku-overview-item-label">
              Service Materials
            </div>
            <div className="multi-sku-overview-item-content">
              <strong>[SKU & Size]</strong>
            </div>
          </div> */}

          <div className="multi-sku-overview-item">
            <div className="multi-sku-overview-item-label">
              {t('platform.sku.publishReview.optionTitle.qtyRules')}
            </div>
            <div className="multi-sku-overview-item-content">
              <ul>
                <li>
                  <strong>{t('platform.sku.wizard.defaultDependency')}:</strong>
                  [{curItem.defaultQty}]
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
